// The session check (docs/CONTRACTS.md sections 7 and 16): GET /api/me answers the caller, 401 sends the shell to
// /login. The cookie is HttpOnly; the client never sees or stores a credential.
import { queryOptions } from '@tanstack/react-query'
import type { Me } from '@nw/shared/api/auth'
import { noteBuild } from './build.ts'
import { ensureWorker } from './pwa.ts'

export type SessionState =
  | { kind: 'user'; me: Me; fixture?: true }
  | { kind: 'signed-out' }
  /** /api/me could not be reached: the request never left the device. */
  | { kind: 'offline' }
  /** /api/me answered, but not with a session or a sign-out: the device is online and the server is not answering. */
  | { kind: 'unavailable' }

function looksLikeMe(v: unknown): v is Me {
  const me = v as Partial<Me> | null
  return (
    !!me &&
    typeof me.id === 'number' &&
    typeof me.displayName === 'string' &&
    (me.role === 'owner' || me.role === 'member') &&
    (me.status === 'active' || me.status === 'pending' || me.status === 'disabled')
  )
}

/**
 * Development builds only: when the API cannot be reached, the shell runs on an invented fixture session so it can
 * be viewed without the P1 auth piece. `import.meta.env.DEV` is false in a production build, so the bundler drops
 * this branch and the fixture module with it (apps/web/scripts/measure.mjs fails if it is ever shipped).
 */
async function unreachable(kind: 'offline' | 'unavailable'): Promise<SessionState> {
  if (import.meta.env.DEV) {
    const { DEV_FIXTURE_ME } = await import('./dev-session.ts')
    return { kind: 'user', me: DEV_FIXTURE_ME, fixture: true }
  }
  return { kind }
}

export async function fetchSession(): Promise<SessionState> {
  let res: Response
  try {
    res = await fetch('/api/me', { credentials: 'same-origin', cache: 'no-store', headers: { accept: 'application/json' } })
  } catch {
    return unreachable('offline')
  }
  noteBuild(res)
  if (res.status === 401) return { kind: 'signed-out' }
  if (!res.ok) return unreachable('unavailable')
  // An answer that is not a Me from a release other than this client's is a contract it does not know: reload into the new one.
  const body: unknown = (res.headers.get('content-type') ?? '').includes('application/json') ? await res.json().catch(() => null) : null
  if (looksLikeMe(body)) {
    ensureWorker() // signing out removed the worker (Clear-Site-Data); a signed-in person has the shell offline again
    return { kind: 'user', me: body }
  }
  noteBuild(res, false)
  return unreachable('unavailable')
}

/**
 * One query for the session; networkMode 'always' so an offline device gets an answer instead of a paused query.
 * Only a signed-in answer is kept fresh for a minute: signed-out, offline and unavailable are re-asked at the next navigation.
 * After sign-in or sign-out, invalidate `sessionQuery.queryKey`.
 */
export const sessionQuery = queryOptions({
  queryKey: ['me'] as const,
  queryFn: fetchSession,
  staleTime: (query) => (query.state.data?.kind === 'user' ? 60_000 : 0),
  networkMode: 'always',
  retry: false,
})
